// store/callListSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/** Where a queue entry came from — decides the call's origin (`list` vs `follow_up`). */
export type CallListEntryOrigin = 'list' | 'followUp'

export interface CallListEntry {
  id: number
  company_name: string
  /** `followUp`: injected by a due-todo alert or started from the todos page. undefined on entries persisted before this field existed. */
  origin?: CallListEntryOrigin
  /** The todo whose alert put this lead into the queue (origin `followUp`, real todos only). */
  followUpTodoId?: number
}

export type ListSource =
  | { type: 'followUp' }
  | { type: 'customList'; customListId: number }
  /** Ad-hoc list of lead ids (no claiming, no load-more); calls count as manual. */
  | { type: 'adHoc' }
  | { type: 'unknown' }

const originForSource = (source: ListSource): CallListEntryOrigin | undefined => {
  if (source.type === 'customList') return 'list'
  if (source.type === 'followUp') return 'followUp'
  return undefined
}

export type PowerDialerState = 'INITIAL' | 'IN_CALL' | 'TIMER_RUNNING' | 'PAUSED'

export const DEFAULT_TIME_TO_NEXT_CALL = 8 * 1000 // 8 seconds

interface CallListState {
  isOpen: boolean
  currentIndex: number
  callList: CallListEntry[]
  startTime: number | null
  listName: string | null
  listSource: ListSource
  isLoadingMore: boolean
  // Powerdialer state
  powerDialerState: PowerDialerState
  timeToNextCall: number
}

const initialState: CallListState = {
  isOpen: false,
  currentIndex: 0,
  callList: [],
  startTime: null,
  listName: null,
  listSource: { type: 'unknown' },
  isLoadingMore: false,
  // Powerdialer initial state
  powerDialerState: 'INITIAL',
  timeToNextCall: DEFAULT_TIME_TO_NEXT_CALL,
}

const callListSlice = createSlice({
  name: 'callList',
  initialState,
  reducers: {
    close(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload
    },
    open(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload
    },
    next(state) {
      if (
        state.callList.length > 0 &&
        state.currentIndex < state.callList.length - 1
      ) {
        state.currentIndex = state.currentIndex + 1
      }
    },
    prev(state) {
      if (state.callList.length > 0 && state.currentIndex > 0) {
        state.currentIndex = state.currentIndex - 1
      }
    },
    setCallList(state, action: PayloadAction<{ leads: CallListEntry[]; listName?: string; listSource?: ListSource }>) {
      const listSource: ListSource = action.payload.listSource || { type: 'unknown' }
      const defaultOrigin = originForSource(listSource)
      // Deduplicate leads by id; stamp the origin the source implies unless the entry says otherwise
      const seen = new Set<number>()
      const uniqueLeads = action.payload.leads
        .filter(l => {
          if (seen.has(l.id)) return false
          seen.add(l.id)
          return true
        })
        .map(l => (l.origin || !defaultOrigin ? l : { ...l, origin: defaultOrigin }))
      state.callList = uniqueLeads
      state.currentIndex = 0
      state.startTime = Date.now()
      state.listName = action.payload.listName || null
      state.listSource = listSource
    },
    appendToCallList(state, action: PayloadAction<CallListEntry[]>) {
      // Deduplicate by id (load-more only exists for custom lists → origin list)
      const existingIds = new Set(state.callList.map(l => l.id))
      const newLeads = action.payload
        .filter(l => !existingIds.has(l.id))
        .map(l => (l.origin ? l : { ...l, origin: 'list' as const }))
      state.callList = [...state.callList, ...newLeads]
      state.isLoadingMore = false
    },
    setLoadingMore(state, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload
    },
    /** Alert bar: a due follow-up slots in right after the current lead. Never twice. */
    insertAtNextIndex(state, action: PayloadAction<CallListEntry>) {
      if (state.callList.some(l => l.id === action.payload.id)) return
      const nextIndex = state.currentIndex + 1
      state.callList.splice(nextIndex, 0, action.payload)
    },
    /**
     * Drop leads from a loaded queue — used after they are marked "erledigt",
     * so the powerdialer can't auto-dial one.
     *
     * The index fixup is the point: a plain filter shifts every later lead up by
     * one, which makes the dialer skip the lead after the removed one. This
     * slice is persisted to localStorage, so a stale queue would otherwise
     * survive reloads indefinitely.
     */
    removeFromCallList(state, action: PayloadAction<number[]>) {
      const drop = new Set(action.payload)
      if (!state.callList.some((lead) => drop.has(lead.id))) return

      const currentId = state.callList[state.currentIndex]?.id
      const remaining = state.callList.filter((lead) => !drop.has(lead.id))

      // Stay on the same lead if it survived; otherwise hold the slot the
      // removed lead occupied so the queue continues forward, not from 0.
      const keptIndex =
        currentId !== undefined && !drop.has(currentId)
          ? remaining.findIndex((lead) => lead.id === currentId)
          : Math.min(state.currentIndex, remaining.length - 1)

      state.callList = remaining
      state.currentIndex = Math.max(0, keptIndex)

      if (remaining.length === 0) {
        state.isOpen = false
        state.powerDialerState = 'INITIAL'
      }
    },
    clearCallList(state) {
      state.callList = []
      state.currentIndex = 0
      state.startTime = null
      state.listName = null
      state.listSource = { type: 'unknown' }
      state.isOpen = false
      state.isLoadingMore = false
      state.powerDialerState = 'INITIAL'
      state.timeToNextCall = DEFAULT_TIME_TO_NEXT_CALL
    },
    // Powerdialer actions
    setPowerDialerState(state, action: PayloadAction<PowerDialerState | { state: PowerDialerState; timeout?: number }>) {
      const newState = typeof action.payload === 'string' ? action.payload : action.payload.state
      const timeout = typeof action.payload === 'object' ? action.payload.timeout : undefined
      state.powerDialerState = newState
      // Reset timer when starting fresh
      if (newState === 'TIMER_RUNNING') {
        state.timeToNextCall = timeout ?? DEFAULT_TIME_TO_NEXT_CALL
      }
    },
    tickPowerDialer(state) {
      if (state.powerDialerState === 'TIMER_RUNNING' && state.timeToNextCall > 0) {
        state.timeToNextCall = state.timeToNextCall - 100
      }
    },
    resetPowerDialerTimer(state, action: PayloadAction<number | undefined>) {
      state.timeToNextCall = action.payload ?? DEFAULT_TIME_TO_NEXT_CALL
    },
    setCurrentIndex(state, action: PayloadAction<number>) {
      const index = action.payload
      if (index >= 0 && index < state.callList.length) {
        state.currentIndex = index
      }
    }
  }
})

export const {
  open,
  next,
  prev,
  setCallList,
  appendToCallList,
  setLoadingMore,
  insertAtNextIndex,
  removeFromCallList,
  clearCallList,
  setPowerDialerState,
  tickPowerDialer,
  resetPowerDialerTimer,
  setCurrentIndex
} = callListSlice.actions
export default callListSlice.reducer

export const selectCallListSource = (state: { callList: CallListState }) => state.callList.listSource
